import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useLang } from '../context/LanguageContext';
import { adminGetUsers, adminBlockUser, adminUnblockUser, adminGetRides, adminCancelRide } from '../services/api';

const STATUS_BADGE = {
  NEW:         'bg-blue-50 text-blue-600',
  ACCEPTED:    'bg-amber-50 text-amber-600',
  ARRIVED:     'bg-orange-50 text-orange-600',
  IN_PROGRESS: 'bg-sky-50 text-sky-600',
  COMPLETED:   'bg-green-50 text-green-600',
  CANCELLED:   'bg-slate-100 text-slate-500',
};

export default function AdminPage({ onBack }) {
  const [tab, setTab]         = useState('users');
  const [users, setUsers]     = useState([]);
  const [rides, setRides]     = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { type: 'block'|'cancel', id, label }
  const { t } = useLang();
  const a = t.admin;

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminGetUsers();
      setUsers(res.data.content || []);
    } catch {
      toast.error('Failed to load users');
    }
  }, []);

  const fetchRides = useCallback(async () => {
    try {
      const res = await adminGetRides(statusFilter);
      setRides(res.data.content || []);
    } catch {
      toast.error('Failed to load rides');
    }
  }, [statusFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchUsers(), fetchRides()]).finally(() => setLoading(false));
  }, [fetchUsers, fetchRides]);

  const handleBlock = async (id, isBlocked) => {
    try {
      await (isBlocked ? adminUnblockUser(id) : adminBlockUser(id));
      await fetchUsers();
      toast.success(isBlocked ? 'User unblocked' : 'User blocked');
    } catch {
      toast.error('Action failed');
    }
    setConfirm(null);
  };

  const handleCancelRide = async (id) => {
    try {
      await adminCancelRide(id);
      await fetchRides();
      toast.success('Ride cancelled');
    } catch {
      toast.error('Action failed');
    }
    setConfirm(null);
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-700 mb-6 transition-colors">
        {a.back}
      </button>
      <h1 className="text-2xl font-black text-slate-900 mb-6">{a.title}</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['users', 'rides'].map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${tab === tabKey ? 'bg-slate-900 text-white' : 'border-2 border-slate-200 text-slate-600 hover:border-slate-400'}`}
          >
            {tabKey === 'users' ? a.tabUsers : a.tabRides}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : tab === 'users' ? (
        <UsersTable users={users} a={a} onAction={(id, isBlocked) => setConfirm({ type: 'block', id, isBlocked, label: isBlocked ? a.unblock : a.block })} />
      ) : (
        <RidesTable
          rides={rides}
          a={a}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          onCancel={(id) => setConfirm({ type: 'cancel', id, label: a.cancelRide })}
          STATUS_BADGE={STATUS_BADGE}
        />
      )}

      {/* Confirm dialog */}
      {confirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <p className="font-semibold text-slate-800 mb-5">
              {confirm.type === 'block' ? a.confirmBlock : a.confirmCancel}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => confirm.type === 'block' ? handleBlock(confirm.id, confirm.isBlocked) : handleCancelRide(confirm.id)}
                className="flex-1 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                {a.yes}
              </button>
              <button
                onClick={() => setConfirm(null)}
                className="flex-1 py-2.5 rounded-full border-2 border-slate-200 text-slate-600 text-sm font-semibold hover:border-slate-400 transition-colors"
              >
                {a.no}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTable({ users, a, onAction }) {
  if (!users.length) return <p className="text-slate-500 text-sm py-8 text-center">{a.noUsers}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="text-left py-3 px-4 font-semibold text-slate-500">{a.name}</th>
            <th className="text-left py-3 px-4 font-semibold text-slate-500">{a.username}</th>
            <th className="text-left py-3 px-4 font-semibold text-slate-500">{a.role}</th>
            <th className="text-left py-3 px-4 font-semibold text-slate-500">{a.status}</th>
            <th className="py-3 px-4" />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="py-3 px-4 font-medium text-slate-800">{u.name}</td>
              <td className="py-3 px-4 text-slate-500">{u.username}</td>
              <td className="py-3 px-4">
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">{u.role}</span>
              </td>
              <td className="py-3 px-4">
                {u.blocked ? (
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-50 text-red-500">{a.blocked}</span>
                ) : (
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-50 text-green-600">{a.active}</span>
                )}
              </td>
              <td className="py-3 px-4 text-right">
                {u.role !== 'ADMIN' && (
                  <button
                    onClick={() => onAction(u.id, u.blocked)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${u.blocked ? 'border-green-300 text-green-600 hover:bg-green-50' : 'border-red-200 text-red-500 hover:bg-red-50'}`}
                  >
                    {u.blocked ? a.unblock : a.block}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RidesTable({ rides, a, statusFilter, onStatusFilter, onCancel, STATUS_BADGE }) {
  const statuses = ['', 'NEW', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => onStatusFilter(s)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border-2 transition-colors ${statusFilter === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>
      {!rides.length ? (
        <p className="text-slate-500 text-sm py-8 text-center">{a.noRides}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 font-semibold text-slate-500">#</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-500">From</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-500">To</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-500">{a.status}</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-500">Price</th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {rides.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-3 px-4 text-slate-400 text-xs">{r.id}</td>
                  <td className="py-3 px-4 font-medium text-slate-800 max-w-[160px] truncate" title={r.fromLocation}>{r.fromLocation}</td>
                  <td className="py-3 px-4 text-slate-600 max-w-[160px] truncate" title={r.toLocation}>{r.toLocation}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_BADGE[r.status] || 'bg-slate-100 text-slate-500'}`}>{r.status}</span>
                  </td>
                  <td className="py-3 px-4 font-semibold text-primary-600">€{r.price != null ? parseFloat(r.price).toFixed(2) : '—'}</td>
                  <td className="py-3 px-4 text-right">
                    {['NEW', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(r.status) && (
                      <button
                        onClick={() => onCancel(r.id)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full border-2 border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                      >
                        {a.cancelRide}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
